// src/components/dashboard/channel-overview.tsx
import { Youtube } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from 'react';
import { useAuth } from '@/features/auth/auth-context';
import { supabase } from '@/lib/supabaseClient'

interface ChannelStats {
  views: number;
  views_change: number;
  subscribers: number;
  subscribers_change: number;
  watch_time_minutes: number;
  watch_time_change: number;
  top_videos: Array<{
    title: string;
    views: number;
  }>;
  last_updated?: string;
}

type TimeRange = '24h' | '7d' | '30d';

export function ChannelOverview() {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [stats, setStats] = useState<ChannelStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
useEffect(() => {
  const fetchChannelStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // DEBUG: Log the API URL
      console.log('API Base URL:', import.meta.env.VITE_API_BASE_URL);

      // Get current user and session
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Session:', session ? 'exists' : 'missing');
      
      if (!session?.user) {
        setError('Please sign in to view channel analytics');
        setLoading(false);
        return;
      }

      // DEBUG: Log the full URL we're calling
      const url = `${import.meta.env.VITE_API_BASE_URL}/youtube-channel-stats?userId=${session.user.id}&timeRange=${timeRange}`;
      console.log('Fetching:', url);

      // Call backend API endpoint
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch' }));
        console.log('Error data:', errorData);
        throw new Error(errorData.error || 'Failed to fetch channel stats');
      }

      const data = await response.json();
      console.log('Received stats:', data);
      setStats(data);
    } catch (err: any) {
      console.error('Failed to fetch channel stats:', err);
      setError(err.message || 'Failed to load channel analytics');
    } finally {
      setLoading(false);
    }
  };

  fetchChannelStats();
}, [user, timeRange]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatChange = (change: number): string => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change}`;
  };

  if (loading) {
    return (
      <div className="bg-surface-dark  rounded-lg p-6 w-full sm:w-auto sm:min-w-[280px] sm:max-w-[340px] lg:max-w-[360px]">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/2"></div>
          <div className="h-4 bg-white/10 rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-12 bg-white/10 rounded"></div>
            <div className="h-12 bg-white/10 rounded"></div>
            <div className="h-12 bg-white/10 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-surface-dark  rounded-lg p-6 w-full sm:w-auto sm:min-w-[280px] sm:max-w-[340px] lg:max-w-[360px]">
        <h3 className="text-lg font-medium text-white mb-4 ">Channel Overview</h3>
        <p className="text-white/60 text-sm">
          {error || 'No channel data available. Upload your first video to see stats!'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface-dark  rounded-lg p-6 w-full sm:w-auto sm:min-w-[280px] sm:max-w-[340px] lg:max-w-[360px]]">
      {/* Header */}
      <div className="flex flex-col items-start gap-4 mb-6">
        <h2 className="text-lg font-medium text-white">Channel Overview</h2>
        <div className="bg-tab-active rounded-md font-medium">
          <Select value={timeRange} onValueChange={(value) => setTimeRange(value as TimeRange)}>
            <SelectTrigger className="w-fit" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>


{/* Stats Grid */}
<div className="space-y-3 pb-6 font-medium">
  <div className="flex justify-between items-center">
    <span className="text-white/60 text-sm ">Views</span>
    <div className="flex items-center gap-2">
      <span className="text-white font-medium">{formatNumber(stats.views)}</span>
      {stats.views_change !== 0 && (
        <span className={`text-xs font-medium ${stats.views_change > 0 ? 'text-green-400' : 'text-red-400'}`}>
          {stats.views_change > 0 ? '+' : ''}{formatNumber(stats.views_change)}
        </span>
      )}
    </div>
  </div>
  
  <div className="flex justify-between items-center">
    <span className="text-white/60 text-sm">Subscribers</span>
    <div className="flex items-center gap-2">
      <span className="text-white font-medium">{formatNumber(stats.subscribers)}</span>
      {stats.subscribers_change !== 0 && (
        <span className={`text-xs font-medium ${stats.subscribers_change > 0 ? 'text-green-400' : 'text-red-400'}`}>
          {stats.subscribers_change > 0 ? '+' : ''}{formatNumber(stats.subscribers_change)}
        </span>
      )}
    </div>
  </div>
  
  <div className="flex justify-between items-center pb-4">
    <span className="text-white/60 text-sm">Watch Time (hours)</span>
    <div className="flex items-center gap-2">
      <span className="text-white font-medium">{Math.round(stats.watch_time_minutes / 60)}</span>
      {stats.watch_time_change !== 0 && (
        <span className={`text-xs font-medium ${stats.watch_time_change > 0 ? 'text-green-400' : 'text-red-400'}`}>
          {stats.watch_time_change > 0 ? '+' : ''}{Math.round(stats.watch_time_change / 60)}
        </span>
      )}
    </div>
  </div>
  <div className="border-t border-border"></div>
</div>

      {/* Top Videos Section */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-white text-sm font-medium">Top videos</h3>
          <span className="text-white/60 text-xs font-medium">Views</span>
        </div>
        {stats.top_videos && stats.top_videos.length > 0 ? (
          <div className="space-y-3">
            {stats.top_videos.slice(0, 3).map((video, idx) => (
              <div key={idx} className="flex justify-between items-start gap-2">
                <span className="text-white/80 text-sm line-clamp-1 flex-1">{video.title}</span>
                <span className="text-white/60 text-xs whitespace-nowrap">{formatNumber(video.views)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/40 text-sm text-center py-4 font-medium">
            No videos yet
          </div>
        )}
      </div>
    </div>
  );
}
